import clsx from "clsx";
import { get } from "lodash-es";
import { type FC, Suspense, lazy, useContext, useEffect, useMemo, useState } from "react";
import { Doc } from "sharedb/lib/client";

import {
  LINKED_SECTION_SEEDING_ACCESS_ID,
  groupAccessIdFromGroupId,
  isInstructorAccessId,
} from "../../../shared/access-id";
import type { EnhancedOp } from "../../../shared/types/sharedb";
import type {
  BlockVariantType,
  CellType,
  FieldType,
  PathType,
  SeededCellType,
  WorksheetType,
} from "../../../shared/types/worksheet";
import {
  DATA_PATH,
  DEFAULT_FIELD_VERSION_NUMBER,
  STATIC_CONTENT_FIELD_TYPES,
  VALUE_KEY,
  VERSIONS_PATH,
  initializeStudentDataForField,
} from "../../../shared/worksheets/constants";
import { instructorFieldIsInitialized } from "../../../shared/worksheets/doc-helpers";
import { fieldProgressForUserCellData } from "../../../shared/worksheets/progress-count";

import { AssetField } from "./AssetField";
import { ChoicesAuthoringField } from "./AuthoringFields";
import { ChoiceField } from "./ChoiceField";
import { EmbedField } from "./Embed/EmbedField";
import { FileField } from "./FileField";
import { ProseMirrorRichTextField } from "./RichText/ProseMirrorRichTextField";
import { QuillRichTextField } from "./RichText/QuillRichTextField";
import { TableField } from "./TableField";

import { ErrorScreen, LoadingTextIndicator } from "components/LoadingScreen";
import { ACCESS_ID_QUERY_PARAM, WAITED_TOO_LONG_TIMEOUT_MS } from "components/constants";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { FeatureButton } from "components/materials/presentation/featuring/FeatureButton";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { CircleCheckIcon, LoadingDotsIcon } from "mds/icons";
import { DocViewContext } from "providers/DocViewProvider";
import { useCapabilities } from "store/capability-hooks";
import { store, useAppSelector } from "store/index";
import {
  pageGroupSelectors,
  pageSelectors,
  selectCurrentSectionIsLinked,
  selectPageAccessDataForAccessId,
  selectWorksheetIsRefreshing,
} from "store/selectors";
import { rollbarAndLogError } from "utils/logger";
import { isGroupPage } from "utils/page-utils";
import { FeatureStatus, usePresentation } from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";
import { getUserFullName } from "utils/user-utils";
import { isNewRichTextEditorEnabled } from "utils/waffle-flags";
import { useUsersFromAccessId } from "utils/worksheet";

const CodeField = lazy(() => import("./Code/CodeField"));
const ChoiceFieldAggregate = lazy(() => import("../Aggregate/ChoiceFieldAggregate"));
const WhiteboardField = lazy(() => import("./Whiteboard/WhiteboardField"));

const DO_NOT_ROLL_UP_FIELDS = STATIC_CONTENT_FIELD_TYPES;

interface FieldComponentProps {
  fieldPath: PathType;
  doc: Doc<WorksheetType>;
  path: PathType;
  blockId: string;
  blockHeight: number;
  blockType: BlockVariantType;
}

const FieldComponent: FC<FieldComponentProps> = ({
  fieldPath,
  doc,
  path,
  blockId,
  blockHeight,
  blockType,
}) => {
  const {
    showResponses,
    canAuthorPage,
    isAuthoringPage,
    canInteractAsStudent,
    isProjecting,
    readOnly,
    accessId,
    isFeaturing,
  } = useContext(DocViewContext);
  const field = get(doc.data, fieldPath) as FieldType;
  const type = field.t;
  const isLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  const worksheetIsRefreshing = useAppSelector(selectWorksheetIsRefreshing);
  const shouldUseInstructorSeededData =
    isLinkedSection &&
    accessId !== LINKED_SECTION_SEEDING_ACCESS_ID &&
    !STATIC_CONTENT_FIELD_TYPES.includes(type);
  const instructorEditingSeededData =
    isInstructorAccessId(accessId) && shouldUseInstructorSeededData;

  const seededDataPath = useMemo(
    () =>
      isLinkedSection
        ? ([DATA_PATH, LINKED_SECTION_SEEDING_ACCESS_ID, field.id] as PathType)
        : undefined,
    [field.id, isLinkedSection],
  );

  // fieldUserData may be undefined if either:
  // - The field data hasn't been initialized yet (for various reasons)
  // - The access ID data hasn't been initialized yet (for various reasons)
  // e.g. a Submission page collected for a user who never accessed the page, or simply
  // during the loading process of the page, while we're waiting for initialization.
  const fieldUserData = instructorEditingSeededData
    ? (get(doc.data, seededDataPath!) as CellType)
    : (get(doc.data, path) as CellType);

  const missingSeededWrapper =
    instructorEditingSeededData && !get(doc.data, seededDataPath!.slice(0, 2));

  // This determines whether we initialize user-specific data for this field for the current
  // accessId. This should only happen once per field per user/group, on first access, exists.
  const [initError, setInitError] = useState<string | null>(null);
  const needsFieldDataInit =
    !initError &&
    !fieldUserData &&
    !readOnly &&
    !STATIC_CONTENT_FIELD_TYPES.includes(type) &&
    !missingSeededWrapper;

  const instructorHasSeededData = shouldUseInstructorSeededData
    ? instructorFieldIsInitialized(doc, field.id)
    : false;

  // If we are in readonly mode and haven't had a chance to initialize the field data yet,
  // we show the template data as the fallback user content, until user content is initialized
  // on the next non-readonly session.
  const shouldShowTemplateData =
    (canAuthorPage || isProjecting || !accessId || (!fieldUserData && readOnly)) &&
    !showResponses &&
    !canInteractAsStudent;

  // If we need to show template data on a linked section, we pull it from the instructor-seeding access ID.
  const userFieldPath = shouldShowTemplateData
    ? instructorHasSeededData || instructorEditingSeededData
      ? (seededDataPath as PathType)
      : fieldPath
    : path;

  // Re-run when type/data come back after forceRefreshDoc (same doc ref). Boolean for effect deps.
  const docReady = Boolean(doc.data) && Boolean(doc.type);

  // We don't know what users will access a document ahead of time, so we initialize
  // student paths ad-hoc, when a component using it is first mounted.
  // We use "oid" (object insert default) instead of "oi" - this is a custom operation
  // that only inserts if the path doesn't exist, preventing race conditions when
  // multiple clients try to initialize the same field simultaneously.
  useEffect(() => {
    if (!needsFieldDataInit) {
      return;
    }

    // forceRefreshDoc clears type (and data); ShareDB rejects submitOp when !doc.type.
    // Do not require doc.version: after doc.create() the client has type+data before version is set.
    const isRefreshing = selectWorksheetIsRefreshing(store.getState());
    if (!docReady || isRefreshing) {
      return;
    }

    const createInitializationOps = (initPath: PathType): EnhancedOp[] => {
      const ops: EnhancedOp[] = [];

      // If there's already seeded data from the instructor we should initialize with that.
      if (instructorHasSeededData) {
        const seededPath =
          field.t === "CodeField" ? seededDataPath! : [...seededDataPath!, VALUE_KEY];

        const seededData = get(doc.data, seededPath) as CellType;
        ops.push({ p: initPath, oid: seededData });
      } else {
        let defaultUserData = initializeStudentDataForField(field);

        // For an instructor we need to allow for initialization so we store the data in the value key
        if (instructorEditingSeededData && field.t !== "CodeField") {
          defaultUserData = {
            [VALUE_KEY]: defaultUserData,
            initialized: false,
          } as SeededCellType;
        }

        ops.push({ p: initPath, oid: defaultUserData });
      }

      const versionsPath = [...initPath.slice(0, -1), VERSIONS_PATH];
      const versionsData = get(doc.data, versionsPath) as Record<string, number>;
      if (versionsData && !versionsData[field.id]) {
        const fieldVersionOp = {
          p: [...versionsPath, field.id],
          // There are old Pages created before August 12, 2024 that don't have
          // version data on some fields and fails to initialize. This is to
          // prevent them from breaking in this situation.
          oid: field.ver || DEFAULT_FIELD_VERSION_NUMBER,
        };

        ops.push(fieldVersionOp);
      }

      return ops;
    };

    // If it's an instructor editing seeded data, we need to initialize the seeded data access id otherwise the
    // user's access id will be initialized.
    const initPath = instructorEditingSeededData ? seededDataPath! : path;
    let ops = createInitializationOps(initPath);

    // If it's a code block we need to initialize 2 access ids (instructor (i) and instructor seeded data (s)).
    // We want students to get the seeded data but we don't want them to have access to the kernel info so
    // we need that in the instructor access id
    if (instructorEditingSeededData && field.t === "CodeField") {
      const instructorOps = createInitializationOps(path);
      ops = [...ops, ...instructorOps];
    }

    // If we somehow take longer than three seconds to do this, assume it's a backend issue,
    // and give the user a chance to retry.
    const timeout = setTimeout(() => {
      setInitError("Timeout");
      rollbarAndLogError("Field data init error", {
        err: "useEffect timeout",
        path: path.join("."),
        docId: doc.id,
      });
    }, WAITED_TOO_LONG_TIMEOUT_MS);

    doc.submitOp(ops, {}, (err) => {
      clearTimeout(timeout);
      if (err) {
        setInitError(err.message);
        rollbarAndLogError("Field data init error", {
          err: err.message,
          path: path.join("."),
          docId: doc.id,
        });
      }
    });

    return () => clearTimeout(timeout);
  }, [
    path,
    needsFieldDataInit,
    instructorEditingSeededData,
    seededDataPath,
    instructorHasSeededData,
    accessId,
    field,
    doc,
    docReady,
    worksheetIsRefreshing,
  ]);

  // RichText and Table fields expect their data to be nested under a "v" key.
  // For student content paths in these fields, we omit the "v" nested key,
  // because we store just the data and not the field metadata.
  const dataPathForRichText = useMemo(() => {
    // Text fields are static content stored in the block definition.
    if (type === "Text") {
      return [...fieldPath, VALUE_KEY];
    }

    if (shouldShowTemplateData) {
      return [...userFieldPath, VALUE_KEY];
    }

    return userFieldPath;
  }, [shouldShowTemplateData, userFieldPath, type, fieldPath]);

  // If an error happens during initialization, instead of re-trying automatically and possibly
  // getting stuck in a loop, allow for manual retries, and show a graceful message.
  if (initError) {
    return (
      <ErrorScreen retryText={t("worksheet.error.try_again")} onRetry={() => setInitError(null)}>
        <div className="body-m">{t("worksheet.error.field_init")}</div>
      </ErrorScreen>
    );
  }

  // If we decided to fire the useEffect for "needsFieldDataInit", wait until the
  // submitOp is through and the fieldUserData is initialized before rendering the field.
  if (needsFieldDataInit) {
    // TODO: Set the height of the loading screen to ESTIMATED_AVG_FIELD_HEIGHT_PX_MAP
    return (
      <LoadingTextIndicator className="w-full">
        {t("worksheet.loading.field_init")}
      </LoadingTextIndicator>
    );
  }

  if (type === "Text") {
    if (isNewRichTextEditorEnabled()) {
      return (
        <ProseMirrorRichTextField
          characterCounter="NONE"
          className="field-ProseMirrorText"
          doc={doc}
          offerEmbedOnPasteURL={isAuthoringPage}
          path={dataPathForRichText}
          placeHolderText={
            blockType === "Instructions"
              ? t("worksheet.placeholder.content")
              : t("worksheet.placeholder.question")
          }
          readOnly={!isAuthoringPage || isFeaturing}
          showPlaceholder={canAuthorPage}
          {...field}
        />
      );
    }
    return (
      <QuillRichTextField
        characterCounter="NONE"
        doc={doc}
        offerEmbedOnPasteURL={isAuthoringPage}
        path={dataPathForRichText}
        placeHolderText={
          blockType === "Instructions"
            ? t("worksheet.placeholder.content")
            : t("worksheet.placeholder.question")
        }
        readOnly={!isAuthoringPage || isFeaturing}
        showPlaceholder={canAuthorPage}
        {...field}
      />
    );
  }

  if (type === "RichTextField") {
    // If we're showing responses, do not show the character counter.
    const characterCounter = showResponses
      ? "NONE"
      : // Otherwise, the character counter is normally only readable, not editable.
        // But instructors can edit the character counter if edit mode is on
        // and the block is not being featured.
        isAuthoringPage && !isFeaturing
        ? "EDIT"
        : "READ";
    // Note the character counter will never be shown if no maxLength is set
    // and the counter is in a read-only state.
    if (isNewRichTextEditorEnabled()) {
      return (
        <ProseMirrorRichTextField
          characterCounter={characterCounter}
          doc={doc}
          fieldPath={fieldPath}
          path={dataPathForRichText}
          placeHolderText={t("worksheet.placeholder.response")}
          showPlaceholder={!canAuthorPage}
          {...field}
        />
      );
    }
    return (
      <QuillRichTextField
        characterCounter={characterCounter}
        dataPathForCounter={fieldPath}
        doc={doc}
        path={dataPathForRichText}
        placeHolderText={t("worksheet.placeholder.response")}
        showPlaceholder={!canAuthorPage}
        {...field}
      />
    );
  }

  if (type === "TableField") {
    return <TableField {...field} doc={doc} fieldPath={fieldPath} path={dataPathForRichText} />;
  }

  if (type === "CodeField") {
    return <CodeField {...field} doc={doc} path={userFieldPath} />;
  }

  // The rest of the fields don't have collaborative data, or don't have collaborative data
  // that is used as a seed for students.

  if (type === "ChoiceField") {
    return isAuthoringPage && !isFeaturing ? (
      <ChoicesAuthoringField {...field} doc={doc} path={fieldPath} />
    ) : (
      <ChoiceField {...field} doc={doc} path={path} />
    );
  }

  if (type === "FileField") {
    return <FileField {...field} doc={doc} path={path} />;
  }

  if (type === "AssetField") {
    return (
      <AssetField
        {...field}
        blockHeight={blockHeight}
        blockId={blockId}
        doc={doc}
        path={fieldPath}
      />
    );
  }

  if (type === "EmbedField") {
    return <EmbedField {...field} doc={doc} path={fieldPath} />;
  }

  if (type === "WhiteboardField") {
    return <WhiteboardField blockId={blockId} fieldId={field.id} />;
  }

  throw new Error(`Field type "${String(type)}" is not supported.`);
};

interface DocAuthorListProps {
  accessId: string;
  groupIndex?: number;
  showLink: boolean;
  page: PageType;
}

const DocAuthorList: FC<DocAuthorListProps> = ({ accessId, groupIndex, showLink, page }) => {
  const users = useUsersFromAccessId(accessId, page);
  const { setQueryParams } = useQueryParamsManager();
  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForAccessId(s, page.id, accessId),
  );
  const isDone = Boolean(pageAccessData?.completed_at);

  if (!users) {
    // This happens when we create groups, delete them, and then re-create new groups while
    // students are in them, or when users get removed from a course after they have already
    // added data. We log this for debugging but it's common enough we don't need to report it.
    console.warn("DocAuthorList: Missing user data", { accessId, page });
    return (
      <div className="flex items-center pb-2">
        <div className="body-s-bold">{t("field.no_authors")}</div>
      </div>
    );
  }

  const handleClick = () => {
    setQueryParams({ [ACCESS_ID_QUERY_PARAM]: accessId });
  };

  const showGroupText = groupIndex !== undefined && !users?.[0]?.is_anonymized;
  const usernameList = users.map(getUserFullName).join(", ");
  const linkText = showGroupText
    ? `${t("common.group_title", { number: groupIndex + 1 })}:`
    : usernameList;
  const textBaseName = clsx("body-s-bold whitespace-nowrap", !showGroupText && "truncate");

  return (
    <div className="flex items-center gap-1 pb-2">
      {showLink ? (
        <button
          className={`text-blue hover:text-blue-shade-20 ${textBaseName}`}
          type="button"
          onClick={handleClick}
        >
          {linkText}
        </button>
      ) : (
        <span className={textBaseName}>{linkText}</span>
      )}

      {showGroupText && <span className="body-s truncate text-black-tint-40">{usernameList}</span>}

      {isDone && (
        <Tooltip content={t("common.completed")}>
          <CircleCheckIcon className="text-black-tint-40" />
        </Tooltip>
      )}
    </div>
  );
};

interface FieldProps {
  fieldPath: PathType;
  accessIdPath: PathType;
  doc: Doc<WorksheetType>;
  blockId: string;
  blockType: BlockVariantType;
  blockHeight: number;
  responseAccessIds: string[];
}

export const Field: FC<FieldProps> = ({
  doc,
  fieldPath,
  blockId,
  blockType,
  accessIdPath,
  blockHeight,
  responseAccessIds,
}) => {
  const { accessId, pageId, isProjecting, showResponses } = useContext(DocViewContext);
  const { canAuthorSection } = useCapabilities();
  const pageGroups = useAppSelector((s) => pageGroupSelectors.filterByPageId(s, pageId));
  const pageGroupAccessIds = pageGroups.map((group) => groupAccessIdFromGroupId(group.id));

  const field = get(doc.data, fieldPath) as FieldType;

  // Arrays get re-created on render, so we memoize to prevent prop-based re-renders in Children
  // Path that the Field will use to read/write student content.
  const id = field?.id;
  const fieldUserDataPath = useMemo(() => [...accessIdPath, id], [accessIdPath, id]);
  const page = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const { isPresenting, featureStatus } = usePresentation();

  // If we are projecting we can't author the page
  const isMdOrLarger = useIsMdOrLarger();
  const canFeature =
    isMdOrLarger &&
    !isProjecting &&
    isPresenting &&
    (showResponses === "ALL" ||
      featureStatus === FeatureStatus.PARTICIPANT_RESPONSE ||
      (featureStatus === FeatureStatus.NONE && showResponses === "ONE"));
  const canLinkToStudent = canAuthorSection && featureStatus === FeatureStatus.NONE;

  const type = field?.t;
  const fieldClassName = `field-${type}`;
  const fieldDebugAttrs = {
    "data-testid": fieldPath.join("."),
    "data-user-data-path": fieldUserDataPath.join("."),
  };

  const visibleAccessIds = useMemo(() => {
    if (!page) {
      return [];
    }

    return responseAccessIds
      .filter((currAccessId) => {
        if (
          !type ||
          (isGroupPage(page) && !pageGroupAccessIds.includes(currAccessId) && !page?.is_imported)
        ) {
          return false;
        }

        // Skip any access ids with empty entries for the given cell.
        const componentPath = [DATA_PATH, currAccessId, id];
        return fieldProgressForUserCellData(type, get(doc.data, componentPath) as CellType).touched;
      })
      .sort((a, b) => {
        const aPageGroupIndex = pageGroupAccessIds.indexOf(a);
        const bPageGroupIndex = pageGroupAccessIds.indexOf(b);

        // If there are group responses, display groups over individual responses.
        if (aPageGroupIndex !== -1 && bPageGroupIndex === -1) return -1;
        if (aPageGroupIndex === -1 && bPageGroupIndex !== -1) return 1;
        if (aPageGroupIndex === -1 && bPageGroupIndex === -1) return a.localeCompare(b);
        return aPageGroupIndex - bPageGroupIndex;
      });
  }, [responseAccessIds, pageGroupAccessIds, doc.data, id, page, type]);

  if (!field || !page) {
    return;
  }

  const skipRollup = DO_NOT_ROLL_UP_FIELDS.includes(type);
  if (!showResponses || skipRollup) {
    return (
      // TODO: Add a suspense "component loading" animation.
      // TODO: We should React.lazy import all components that can get rendered here.
      // TODO: Also use the estimated base field height as the height of loading screen,
      //       see ESTIMATED_AVG_FIELD_HEIGHT_PX_MAP
      <Suspense fallback={null}>
        <div className={`${fieldClassName}`} {...fieldDebugAttrs}>
          <FieldComponent
            blockHeight={blockHeight}
            blockId={blockId}
            blockType={blockType}
            doc={doc}
            fieldPath={fieldPath}
            path={fieldUserDataPath}
          />
        </div>
      </Suspense>
    );
  }

  if (type === "ChoiceField" && showResponses === "ALL") {
    return (
      <Suspense fallback={<LoadingDotsIcon />}>
        <ChoiceFieldAggregate {...field} doc={doc} fieldPath={fieldPath} page={page} />
      </Suspense>
    );
  }

  if (showResponses === "ONE") {
    const componentPath = [DATA_PATH, accessId, id];
    return (
      <Suspense fallback={null}>
        <div className={fieldClassName} {...fieldDebugAttrs}>
          <FieldComponent
            blockHeight={blockHeight}
            blockId={blockId}
            blockType={blockType}
            doc={doc}
            fieldPath={fieldPath}
            path={componentPath}
          />
        </div>
      </Suspense>
    );
  }

  if (visibleAccessIds.length === 0) {
    return (
      <div className="rounded-xl bg-black-tint-97 p-3 text-black-tint-40">
        {t("field.no_responses")}
      </div>
    );
  }

  return visibleAccessIds.map((currAccessId, index) => {
    const componentPath = [DATA_PATH, currAccessId, id];
    const studentFieldId = `${currAccessId}-${id}`;
    const emptyField = !fieldProgressForUserCellData(type, get(doc.data, componentPath) as CellType)
      .touched;
    const groupIndex = pageGroupAccessIds.indexOf(currAccessId);

    return (
      <div className="relative" key={studentFieldId}>
        {/* The feature button has a z-index of 400 to ensure it is on top of the Tldraw whiteboard. */}
        <div className="absolute -left-9 top-0 z-[400]">
          {canFeature && (
            <FeatureButton
              accessId={currAccessId}
              blockId={blockId}
              blockType={blockType}
              isRollup={Boolean(showResponses)}
              pageId={pageId}
            />
          )}
        </div>

        <div
          className={clsx(
            "response-container rounded-xl bg-black-tint-97 p-3",
            index < visibleAccessIds.length - 1 && "mb-2",
          )}
          // Used for marker scrolling
          id={`userblock-${blockId}-${currAccessId}`}
        >
          <HoverableToolbar uncentered>
            <div className="w-full">
              <DocAuthorList
                accessId={currAccessId}
                groupIndex={groupIndex === -1 ? undefined : groupIndex}
                page={page}
                showLink={canLinkToStudent}
              />

              {!emptyField && (
                <Suspense fallback={null}>
                  <div className={fieldClassName} {...fieldDebugAttrs}>
                    <FieldComponent
                      blockHeight={blockHeight}
                      blockId={blockId}
                      blockType={blockType}
                      doc={doc}
                      fieldPath={fieldPath}
                      path={componentPath}
                    />
                  </div>
                </Suspense>
              )}
            </div>
          </HoverableToolbar>
        </div>
      </div>
    );
  });
};
